import { promises as fs } from 'node:fs';
import type { TraceBuilder, RecentTraceCallsResponse } from '../types';

/**
 * Read file in reverse order (from end to beginning)
 */
export async function readFileReverse(
  filePath: string,
  chunkSize: number,
  processLine: (line: string) => void,
): Promise<void> {
  const handle = await fs.open(filePath, 'r');

  try {
    const stats = await handle.stat();
    let position = stats.size;
    let remainder = '';

    while (position > 0) {
      const length = Math.min(chunkSize, position);
      position -= length;

      const buffer = Buffer.alloc(length);
      await handle.read(buffer, 0, length, position);

      let chunk = buffer.toString('utf8');
      if (remainder) {
        chunk += remainder;
        remainder = '';
      }

      const lines = chunk.split('\n');
      remainder = lines.shift() ?? '';

      for (let i = lines.length - 1; i >= 0; i -= 1) {
        if (lines[i]) {
          processLine(lines[i]);
        }
      }
    }

    if (remainder) {
      processLine(remainder);
    }
  } finally {
    await handle.close();
  }
}

/**
 * Build paginated response from trace builders
 */
export function buildPaginatedResponse(
  items: TraceBuilder[],
  page: number,
  pageSize: number,
): RecentTraceCallsResponse {
  const totalItems = items.length;
  const totalPages = totalItems === 0 ? 0 : Math.ceil(totalItems / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  const pagedItems = items.slice(startIndex, endIndex).map((builder) => ({
    traceId: builder.traceId,
    method: builder.method,
    path: builder.path,
    startTime: builder.startTime,
    endTime: builder.endTime,
    statusCode: builder.statusCode,
    durationMs: builder.durationMs,
    entries: builder.entries.slice().reverse(),
  }));

  return {
    page,
    pageSize,
    totalCalls: totalItems,
    totalPages,
    calls: pagedItems,
  };
}
